"use client";

import { useMemo, useState } from "react";
import { BarChart3, Loader2, AlertCircle, Eye, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { useLocation } from "wouter";
import { usePageTitle } from "@/hooks/usePageTitle";

/**
 * MeusResultados — Página de avaliações para o perfil client_rh
 * Exibe apenas as avaliações da empresa vinculada ao usuário logado
 * Reutiliza a procedure evaluations.list que já filtra por companyId para client_rh
 */
export default function MeusResultados() {
  usePageTitle("Meus Resultados");
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const companyId = (user as any)?.companyId;
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("all");

  const { data: evaluations, isLoading, error } = trpc.evaluations.list.useQuery(undefined, {
    enabled: !!user,
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
      case "AVALIACAO_CONCLUIDA":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle2 className="w-3 h-3" /> Avaliação Concluída
          </span>
        );
      case "pending":
      case "AGUARDANDO_RESPOSTAS":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" /> Aguardando Respostas
          </span>
        );
      case "in_progress":
      case "AVALIACAO_EM_PROCESSAMENTO":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            <Clock className="w-3 h-3" /> Em Processamento
          </span>
        );
      case "AVALIACAO_BLOQUEADA_SEM_RESPOSTAS":
      case "AVALIACAO_BLOQUEADA_DADOS_INSUFICIENTES":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" /> Bloqueada
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <XCircle className="w-3 h-3" /> {status}
          </span>
        );
    }
  };

  const evaluationsList = Array.isArray(evaluations) ? evaluations : (evaluations as any)?.items || [];
  const companyOptions = useMemo(
    () =>
      evaluationsList
        .map((evaluation: any) => ({
          id: String(evaluation.companyId),
          name: evaluation.companyTradeName || evaluation.companyName || `Empresa ${evaluation.companyId}`,
        }))
        .filter((company: any, index: number, array: any[]) => array.findIndex((item) => item.id === company.id) === index)
        .sort((a: any, b: any) => a.name.localeCompare(b.name, "pt-BR")),
    [evaluationsList],
  );
  const filteredEvaluations = useMemo(
    () => evaluationsList.filter((evaluation: any) => selectedCompanyId === "all" || String(evaluation.companyId) === selectedCompanyId),
    [evaluationsList, selectedCompanyId],
  );

  return (
    <SalviaLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-7 h-7 text-[#6B47DC]" />
            <h1 className="text-2xl font-bold text-gray-900">Resultados das Avaliações</h1>

        </div>

        {!companyId && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Empresa não vinculada</p>
              <p className="text-sm text-yellow-700 mt-1">
                Seu usuário ainda não está vinculado a uma empresa. Contate o administrador do canal.
              </p>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#6B47DC]" />
            <span className="ml-3 text-gray-500">Carregando avaliações...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Erro ao carregar avaliações</p>
              <p className="text-sm text-red-700 mt-1">{error.message}</p>
            </div>
          </div>
        )}

        {!isLoading && !error && evaluationsList.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhuma avaliação encontrada</p>
            <p className="text-sm text-gray-400 mt-1">
              As avaliações das empresas do seu escopo aparecerão aqui quando forem realizadas.
            </p>
          </div>
        )}

        {!isLoading && !error && evaluationsList.length > 0 && (
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Filtro por cliente</p>
              <p className="text-xs text-muted-foreground">
                Selecione uma empresa do seu escopo hierárquico para refinar a listagem de avaliações.
              </p>
            </div>
            <div className="w-full sm:w-80">
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as empresas do escopo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as empresas do escopo</SelectItem>
                  {companyOptions.map((company: any) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {filteredEvaluations.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Cargo/Função</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Setor</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Empresa</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Data</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvaluations.map((evaluation: any) => (
                    <tr key={evaluation.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {evaluation.jobTitle || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {evaluation.sector || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="font-medium text-gray-900">{evaluation.companyTradeName || evaluation.companyName || "—"}</div>
                        <div className="text-xs text-gray-500">{evaluation.companyCnpj || `ID ${evaluation.companyId}`}</div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(evaluation.status)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {evaluation.createdAt
                          ? new Date(evaluation.createdAt).toLocaleDateString("pt-BR")
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {(evaluation.status === "completed" || evaluation.status === "AVALIACAO_CONCLUIDA") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/evaluations/${evaluation.id}/resultado`)}
                            className="text-[#6B47DC] hover:text-[#5a3bc0]"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver Resultado
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Aviso de somente leitura */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
          <p className="text-sm text-blue-700">
            Você está visualizando os resultados das empresas do seu escopo. Use o filtro por cliente para focar em uma empresa específica quando necessário.
          </p>
        </div>
      </div>
    </SalviaLayout>
  );
}
